/**
 * @file     DisclosureSection.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Reusable disclosure surface for detail-page sections: bottom
 *           sheet (Drawer, rounded top, drag handle) on mobile, in-place
 *           Collapse on desktop. Two usage modes:
 *             - self-managed: pass `summary` and it renders a tappable
 *               summary row that toggles the content;
 *             - controlled: pass `open`/`onOpenChange` and drive it from
 *               an external trigger elsewhere on the page.
 *           Cross-component open requests go through openDisclosure(id)
 *           (a window CustomEvent), so a trigger far away in the tree
 *           needs no context plumbing.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import { glassMaterial } from '@/design-system/tokens/glass';
import * as React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { glassBlur, zLayers } from '@/design-system/tokens';

const OPEN_EVENT = 'relta:disclosure-open';
const STATE_EVENT = 'relta:disclosure-state';
const TRANSITION_MS = 220;

/** Ask the DisclosureSection with this id to open (and scroll into view in collapse mode). */
export function openDisclosure(id: string): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { id } }));
}

/** Subscribe to a disclosure's open/closed state (for remote aria-expanded). */
export function useDisclosureState(id: string): boolean {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string; open: boolean }>).detail;
      if (detail?.id === id) setOpen(detail.open);
    };
    window.addEventListener(STATE_EVENT, onState);
    return () => window.removeEventListener(STATE_EVENT, onState);
  }, [id]);
  return open;
}

interface Props {
  /** Stable id: DOM id of the wrapper + key for openDisclosure() events. */
  id: string;
  children: React.ReactNode;
  /** Tappable summary row (self-managed mode). Omit when fully controlled. */
  summary?: React.ReactNode;
  summaryAriaLabel?: string;
  /** Heading shown at the top of the mobile bottom sheet. */
  sheetTitle?: string;
  /** Controlled mode. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 'sheet' (default): bottom Drawer on mobile, in-place Collapse on desktop.
   * 'collapse': in-place Collapse on ALL viewports — the accordion treatment
   * for long-form insight panels that shouldn't live in a bottom sheet.
   */
  variant?: 'sheet' | 'collapse';
  /** Initial open state in self-managed mode (ignored when controlled). */
  defaultOpen?: boolean;
  /**
   * Render the whole disclosure as its own distinct card surface: the
   * summary row becomes the card header (borderless, full-bleed) and the
   * expanded content gets matching horizontal padding. Used on the detail
   * page body so each accordion reads as an independent card instead of a
   * row inside a shared container.
   */
  card?: boolean;
}

export function DisclosureSection({
  id,
  children,
  summary,
  summaryAriaLabel,
  sheetTitle,
  open: controlledOpen,
  onOpenChange,
  variant = 'sheet',
  defaultOpen = false,
  card = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const useSheet = variant === 'sheet' && isMobile;
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : uncontrolledOpen;
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  // Broadcast state so remote triggers can reflect aria-expanded.
  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: { id, open } }));
  }, [id, open]);

  // Remote open requests (openDisclosure(id)).
  React.useEffect(() => {
    const onOpenRequest = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (detail?.id !== id) return;
      setOpen(true);
      if (!useSheet) {
        // Let Collapse start expanding before scrolling to it.
        requestAnimationFrame(() => {
          rootRef.current?.scrollIntoView({
            behavior: reducedMotion ? 'auto' : 'smooth',
            block: 'start',
          });
        });
      }
    };
    window.addEventListener(OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_EVENT, onOpenRequest);
  }, [id, useSheet, reducedMotion, setOpen]);

  const timeout = reducedMotion ? 0 : TRANSITION_MS;

  // In card mode the expanded content inherits the card's horizontal
  // padding (the sheet Drawer already pads its own body).
  const inner =
    card && !useSheet ? (
      <Box sx={{ px: { xs: 4, md: 5 }, pb: { xs: 4, md: 5 } }}>{children}</Box>
    ) : (
      children
    );

  const content = useSheet ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      sx={{ zIndex: zLayers.leadCapture }}
      transitionDuration={timeout}
      PaperProps={{
        sx: {
          ...glassMaterial('thick'),
          borderTopLeftRadius: `${layoutTokens.radius.xl}px`,
          borderTopRightRadius: `${layoutTokens.radius.xl}px`,
          maxHeight: '88dvh',
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      {/* Drag handle */}
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 4,
          borderRadius: `${layoutTokens.radius.pill}px`,
          bgcolor: 'divider',
          mx: 'auto',
          mt: 1,
          mb: sheetTitle ? 1 : 0.5,
          flexShrink: 0,
        }}
      />
      {sheetTitle ? (
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{ fontWeight: 600, textAlign: 'center', px: 2, pb: 0.5 }}
        >
          {sheetTitle}
        </Typography>
      ) : null}
      <Box sx={{ overflowY: 'auto', px: 2, pb: 2 }}>{children}</Box>
    </Drawer>
  ) : (
    <Collapse in={open} timeout={timeout}>
      {inner}
    </Collapse>
  );

  return (
    <Box
      ref={rootRef}
      id={id}
      sx={{
        scrollMarginTop: 96,
        ...(card
          ? {
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${layoutTokens.radius.lg}px`,
              overflow: 'hidden',
              backdropFilter: glassBlur.medium,
              WebkitBackdropFilter: glassBlur.medium,
              boxShadow: '0 10px 32px rgba(0, 0, 0, 0.06)',
            }
          : null),
      }}
    >
      {summary ? (
        <ButtonBase
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`${id}-content`}
          aria-label={summaryAriaLabel}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
            gap: 2,
            px: { xs: 4, md: 5 },
            py: card ? 2 : 1.75,
            // In card mode the surrounding card owns the border; a bordered
            // summary row inside it would read as a double frame.
            borderRadius: card ? 0 : `${layoutTokens.radius.lg}px`,
            border: card ? 'none' : '1px solid',
            borderColor: card ? undefined : 'divider',
            cursor: 'pointer',
            transition: reducedMotion
              ? 'none'
              : 'background-color 200ms ease, border-color 200ms ease',
            '&:hover': { backgroundColor: 'action.hover' },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>{summary}</Box>
          <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden
            sx={{
              width: 18,
              height: 18,
              flexShrink: 0,
              color: 'text.secondary',
              transform: open && !useSheet ? 'rotate(180deg)' : 'none',
              transition: reducedMotion ? 'none' : 'transform 220ms ease',
            }}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </Box>
        </ButtonBase>
      ) : null}
      <Box id={`${id}-content`}>{content}</Box>
    </Box>
  );
}
